import { ChantContext } from "../../gregorio-chant/ChantContext";
import { ChantLayoutElement } from "../layout/ChantLayoutElement";
import { Rect } from "../../drawing";
import { Glyph } from "../glyphs/Glyph";
import { GlyphMeasurer } from "../GlyphMeasurer";
import { calculatePositionFromLine, calculateHeightPositionFromPitch } from "../utils";
import { LyricsTextMeasurer } from "../LyricsTextMeasurer";
import { GregorioGlyph } from "../../gregorio-chant/structures/GregorioGlyph";
import { VisualizerHelper } from "./VisualizerHelper";
import { GregorioNoteElement } from "../../gregorio-chant/structures/GregorioNoteElement";
import { RectangleVisualizer } from "./RectangleVisualizer";
import { GregorioShape } from "../../gregorio-chant/enums/GregorioShape";
import { DistanceConverter } from "../distances/DistanceConverter";

export class GlyphVisualizer extends ChantLayoutElement {
  protected _textAnchor: "start" | "middle" | "end" = "start";
  protected _baseline: "normal" | "middle" | "none" | "bottom";
  protected readonly _glyph: Glyph;
  protected _capHeight: number;
  protected _depth: number;
  protected _color;
  protected readonly _notes: GregorioNoteElement[];
  protected _isCavum = false;
  protected readonly _fuseToNextGlyph: number;
  private _alternations = [
    GregorioShape.FLAT,
    GregorioShape.FLAT_PAREN,
    GregorioShape.NATURAL,
    GregorioShape.NATURAL_PAREN,
    GregorioShape.SHARP,
    GregorioShape.SHARP_PAREN,
  ];

  private _punctumInclinatums = [
    GregorioShape.PUNCTUM_INCLINATUM_ASCENDENS,
    GregorioShape.PUNCTUM_INCLINATUM_AUCTUS,
    GregorioShape.PUNCTUM_INCLINATUM_DEMINUTUS,
    GregorioShape.PUNCTUM_INCLINATUM_DESCENDENS,
    GregorioShape.PUNCTUM_INCLINATUM_STANS,
    GregorioShape.PUNCTUM_INCLINATUM_UNDETERMINED,
  ];

  get isAlternation(): boolean {
    return this.notes.some((n) => this._alternations.includes(n.shape));
  }

  get isQuilisma(): boolean {
    return this.notes.some((n) => n.shape === GregorioShape.QUILISMA);
  }

  get isPunctumInclinatum(): boolean {
    return this.notes.some((n) => this._punctumInclinatums.includes(n.shape));
  }

  get notes(): GregorioNoteElement[] {
    return this._notes;
  }

  get fuseToNextGlyph(): number {
    return this._fuseToNextGlyph;
  }

  override get top(): number {
    return this.bounds.y - this.bounds.height;
  }

  get capHeight(): number {
    return this._capHeight;
  }

  get depth(): number {
    return this._depth;
  }

  _widthOverrides: { [key: number]: number } = {
    58797: 5.5,
  };

  constructor(
    context: ChantContext,
    glyph: Glyph,
    color: string,
    notes: GregorioNoteElement[] = [],
    isCavum = false,
    fuseToNextGlyph = 0,
    baseline: "middle" | "normal" | "none" | "bottom" = "normal"
  ) {
    super(context);

    this._context = context;
    this._glyph = glyph;
    this._capHeight = 0;
    this._depth = 0;
    this._color = color;
    this._notes = notes;
    this._isCavum = isCavum;
    this._baseline = baseline;
    this._fuseToNextGlyph = fuseToNextGlyph;

    this.setBounds();
  }

  public performLayout(prevElements: ChantLayoutElement[]): void {
    const prevElement = VisualizerHelper.findLastGlyphOrSpaceOrBarOrSign(prevElements);
    if (prevElement) {
      this.bounds.setX(prevElement.bounds.right);
    } else {
      if (this.notes.length > 0 && this._alternations.includes(this.notes[0].shape)) {
        const space = new DistanceConverter(this._context).lengthToPx(
          this._context.alterationspace
        );
        this.bounds.setX(-(space + this.bounds.width));
      } else {
        this.bounds.setX(0);
      }
    }
  }

  setStaffPositionFromGlyph(glyph: GregorioGlyph) {
    if (glyph.notes.notes.length > 0) {
      const pitch = glyph.firstNote!.pitch;
      const y = calculateHeightPositionFromPitch(this._context, pitch);
      this.bounds.setY(y);
    }
  }

  setStaffPositionFromNote(note: GregorioNoteElement) {
    const y = calculateHeightPositionFromPitch(this._context, note.pitch);
    this.bounds.setY(y);
  }

  setStaffPosition(pitch: number) {
    const y = calculateHeightPositionFromPitch(this._context, pitch);
    this.bounds.setY(y);
  }

  setPosition(y: number) {
    this.bounds.setY(y);
  }

  setLine(line: number) {
    const y = calculatePositionFromLine(this._context, line);
    this.bounds.setY(y);
  }

  setTextAnchor(textAnchor: "start" | "middle" | "end") {
    this._textAnchor = textAnchor;
  }

  private debugDraw(y: number): SVGElement[] {
    if (this._context.debugGlyphs) {
      const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
      rect.setAttribute("x", String(this.bounds.x));
      rect.setAttribute("y", String(y - this.bounds.height));
      rect.setAttribute("width", String(this.bounds.width));
      rect.setAttribute("height", String(this.bounds.height));
      rect.setAttribute("fill", "none");
      rect.setAttribute("stroke", "red");
      rect.setAttribute("stroke-width", "1");

      return [rect];
    }

    return [];
  }

  public createSvgFragments(): SVGElement[] {
    let y = 0;
    switch (this._baseline) {
      case "normal":
        y = this.bounds.y + this._context.pitchHeight / 2;
        break;
      case "middle":
        y = this.bounds.y + this._capHeight / 2;
        break;
      case "bottom":
        y = this.bounds.y + this.bounds.height;
        break;
      case "none":
        y = this.bounds.y;
        break;
    }

    if (this._glyph.hasError) {
      const rect = new RectangleVisualizer(this._context, Rect.create(this.bounds.x, y, 4, 10));
      return [...rect.createSvgFragments()];
    }

    const text = document.createElementNS("http://www.w3.org/2000/svg", "text");

    text.textContent = String.fromCodePoint(this._glyph.code);
    text.setAttribute("font-family", this._glyph.fontFamily);
    text.setAttribute("dominant-baseline", "text-top");
    text.setAttribute("text-anchor", this._textAnchor);
    // text.setAttribute("rnd-height", String(this.bounds.height));
    // text.setAttribute("rnd-width", String(this.bounds.width));
    // text.setAttribute("rnd-cap", String(this.capHeight));

    if (this._isCavum) {
      text.setAttribute("x", String(this.bounds.x + 1.5 || 0));
      text.setAttribute("y", String(y - 0.85));
      text.setAttribute("font-size", this._glyph.size - 12 + "px");
      text.setAttribute("paint-order", "stroke");
      text.setAttribute("stroke-linecap", "round");
      text.setAttribute("stroke-linejoin", "round");
      text.setAttribute("stroke", this._color);
      text.setAttribute("stroke-width", "3px");
      text.setAttribute("fill", "#ffffff");
    } else {
      text.setAttribute("x", String(this.bounds.x || 0));
      text.setAttribute("y", String(y - 0.7));
      text.setAttribute("font-size", this._glyph.size + "px");
      text.setAttribute("fill", this._color);
    }

    return [text, ...this.debugDraw(y), ...super.createSvgFragments()];
  }

  private setBounds() {
    if (this._glyph.hasError) {
      return;
    }

    const bounds = GlyphMeasurer.instance.measureChar(String.fromCodePoint(this._glyph.code), {
      family: this._glyph.fontFamily,
      size: this._glyph.size,
    });

    this._capHeight = bounds.capHeight;
    this._depth = bounds.depth;

    if (this._glyph.code in this._widthOverrides) {
      this.bounds = Rect.create(0, 0, this._widthOverrides[this._glyph.code], bounds.height);
    } else {
      // todo: remove LyricsTextMeasurer from here
      LyricsTextMeasurer.instance.setElement(this.buildGlyphSvg());
      const params = LyricsTextMeasurer.instance.element.getBoundingClientRect();

      this.bounds = Rect.create(0, 0, params.width, bounds.height);
    }
  }

  // used for width measurement
  private buildGlyphSvg(): SVGElement {
    const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
    text.textContent = String.fromCodePoint(this._glyph.code);
    text.setAttribute("font-family", this._glyph.fontFamily);
    text.setAttribute("font-size", this._glyph.size + "px");
    return text;
  }
}
